import { useState, FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { apiClient } from '@/lib/api-client'
import type {
  UnsubscribePreferencesResponse,
  EmailPreferences,
  UnsubscribeRequest,
  UnsubscribeSuccessResponse,
  UnsubscribeReason,
} from '@/types/unsubscribe'

interface UnsubscribeFormProps {
  userPrefs: UnsubscribePreferencesResponse
  token: string
  email: string
  onSuccess: (response: UnsubscribeSuccessResponse) => void
  onError: (error: string) => void
}

const preferenceLabels = {
  marketing: {
    title: 'Marketing & Promotions',
    description: 'Product updates, feature announcements, newsletters',
  },
  account: {
    title: 'Account & Security',
    description: 'Login alerts, password changes, security notifications',
  },
  product: {
    title: 'Product Updates',
    description: 'New features, maintenance notifications',
  },
  community: {
    title: 'Community & Social',
    description: 'Comments, mentions, collaboration invites',
  },
  system: {
    title: 'System & Technical',
    description: 'Service status, downtime notifications',
  },
}

const reasonOptions: { value: UnsubscribeReason; label: string }[] = [
  { value: 'too_many_emails', label: 'Too many emails' },
  { value: 'not_relevant', label: 'Content not relevant' },
  { value: 'temporary_pause', label: 'Taking a temporary break' },
  { value: 'never_subscribed', label: 'Never subscribed' },
  { value: 'other', label: 'Other' },
]

export function UnsubscribeForm({
  userPrefs,
  token,
  email,
  onSuccess,
  onError,
}: UnsubscribeFormProps) {
  const [preferences, setPreferences] = useState<EmailPreferences>(
    userPrefs.currentPreferences
  )
  const [reason, setReason] = useState<UnsubscribeReason | ''>('')
  const [feedback, setFeedback] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)

  const handlePreferenceChange = (
    key: keyof EmailPreferences,
    checked: boolean
  ) => {
    setPreferences(prev => ({
      ...prev,
      [key]: checked,
    }))
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      const requestData: UnsubscribeRequest = {
        token,
        email,
        preferences,
        reason: (reason as UnsubscribeReason) || undefined,
        feedback: feedback.trim() || undefined,
      }

      const response = await apiClient.updateUnsubscribePreferences(requestData)
      onSuccess(response)
    } catch (error) {
      let errorMessage = 'Failed to update preferences. Please try again.'

      if (error instanceof Error) {
        errorMessage = error.message
      }

      onError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const allDisabled = Object.values(preferences).every(v => !v)

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Email Preferences</CardTitle>
        <CardDescription>
          Manage your email preferences for {email}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <Label className="text-base font-medium">
              Choose which emails you'd like to receive:
            </Label>

            {Object.entries(preferenceLabels).map(([key, config]) => (
              <div
                key={key}
                className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-accent/50 transition-colors focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
              >
                <Checkbox
                  id={key}
                  checked={preferences[key as keyof EmailPreferences]}
                  onCheckedChange={checked =>
                    handlePreferenceChange(
                      key as keyof EmailPreferences,
                      checked as boolean
                    )
                  }
                  className="mt-1 w-5 h-5"
                />
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor={key}
                    className="font-medium cursor-pointer text-sm"
                  >
                    {config.title}
                  </Label>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {config.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {allDisabled && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Note:</strong> You've disabled all email types. You
                won't receive any emails from us.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="reason">
              Why are you changing your preferences? (Optional)
            </Label>
            <Select
              value={reason}
              onValueChange={value =>
                setReason(value as UnsubscribeReason | '')
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {reasonOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback">Additional feedback (Optional)</Label>
            <Textarea
              id="feedback"
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              maxLength={500}
              placeholder="Help us improve by sharing your thoughts..."
              className="min-h-[100px]"
            />
            <p className="text-xs text-muted-foreground">
              {feedback.length}/500 characters
            </p>
          </div>

          <Button type="submit" disabled={submitting} className="w-full">
            {submitting ? 'Updating preferences...' : 'Update Preferences'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
